import React from 'react';
import { Link } from 'react-router-dom';

/**
 * Legal page chrome, matching v1's markup exactly — spacing, type scale,
 * colours and hover states are transcribed from the v1 HTML.
 */

interface LegalLayoutProps {
    title: string;
    lastUpdated: string;
    lede: React.ReactNode;
    /** Second intro paragraph, set at 16px in v1 (the lede above is 17px). */
    intro: React.ReactNode;
    otherHref: string;
    otherLabel: string;
    children: React.ReactNode;
}

const LegalLayout: React.FC<LegalLayoutProps> = ({
    title,
    lastUpdated,
    lede,
    intro,
    otherHref,
    otherLabel,
    children,
}) => {
    return (
        <div className="min-h-screen bg-dark-base text-white">
            {/* fixed pill header — max-width 920px on legal pages */}
            <header className="pointer-events-none fixed inset-x-0 top-0 z-[70] flex justify-center px-4 py-3.5">
                <div className="pointer-events-auto flex h-[60px] w-full max-w-[920px] items-center justify-between rounded-full border border-dark-gunmetal bg-[rgba(27,32,38,0.74)] pl-5 pr-2 shadow-[0_12px_32px_rgba(0,0,0,0.4)] backdrop-blur-[20px]">
                    <Link to="/" aria-label="Fork home" className="flex items-center">
                        <img
                            src="/assets/fork-logo.png"
                            alt="FORK."
                            className="h-[22px] w-auto object-contain"
                        />
                    </Link>
                    <Link
                        to="/"
                        className="inline-flex h-11 items-center gap-2 rounded-full bg-[#3F4346] px-5 text-[11px] font-semibold uppercase tracking-[0.1em] text-white transition-colors hover:bg-[#4A4F53]"
                    >
                        <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden="true"
                        >
                            <path d="M19 12H5" />
                            <path d="M12 19l-7-7 7-7" />
                        </svg>
                        Back to home
                    </Link>
                </div>
            </header>

            <main className="mx-auto max-w-[760px] px-5 pb-[clamp(64px,9vw,120px)] pt-[clamp(130px,18vh,180px)]">
                <span className="text-[10px] font-medium uppercase tracking-[0.16em] text-success">
                    Legal
                </span>
                <h1 className="mt-4 text-[clamp(34px,5vw,56px)] font-extrabold leading-[1.02] tracking-[-0.035em] text-white">
                    {title}
                </h1>
                <p className="mt-4 text-sm text-dark-textSubtle">{lastUpdated}</p>
                <p className="mt-6 text-[17px] leading-[1.65] text-dark-textSecondary">{lede}</p>
                <p className="mt-3.5 text-[16px] leading-[1.65] text-dark-textSecondary">{intro}</p>

                {children}

                <footer className="mt-16 flex flex-wrap items-center justify-between gap-3 border-t border-dark-gunmetal pt-6">
                    <p className="text-[13px] text-dark-textSubtle">
                        © 2026 Fork by InfiniteLoop Technologies.
                    </p>
                    <Link
                        to={otherHref}
                        className="text-[13px] text-dark-textSecondary transition-colors hover:text-white"
                    >
                        {otherLabel}
                    </Link>
                </footer>
            </main>
        </div>
    );
};

/** section > h2 — v1: margin-top 48px, h2 24px/700, numeral in mint. */
export const LegalSection: React.FC<{
    label?: string;
    title: string;
    children: React.ReactNode;
}> = ({ label, title, children }) => (
    <section className="mt-12">
        <h2 className="mb-4 text-2xl font-bold tracking-[-0.02em] text-white">
            {label && <span className="text-success">{label}</span>}
            {label && ' '}
            {title}
        </h2>
        {children}
    </section>
);

/** h3 — v1: margin 26px 0 10px, 18px/700. `flush` drops the top margin. */
export const LegalSubheading: React.FC<{ children: React.ReactNode; flush?: boolean }> = ({
    children,
    flush,
}) => (
    <h3 className={`${flush ? 'mt-0' : 'mt-[26px]'} mb-2.5 text-[18px] font-bold text-white`}>
        {children}
    </h3>
);

/** p — v1: margin 0 0 14px, or margin 0 when last in its section. */
export const LegalText: React.FC<{ children: React.ReactNode; last?: boolean }> = ({
    children,
    last,
}) => (
    <p className={`${last ? 'mb-0' : 'mb-3.5'} text-[16px] leading-[1.65] text-dark-textSecondary`}>
        {children}
    </p>
);

/** ul — v1: margin 0 0 14px, padding-left 22px, grid gap 8px, disc markers. */
export const LegalList: React.FC<{ items: React.ReactNode[] }> = ({ items }) => (
    <ul className="mb-3.5 grid list-disc gap-2 pl-[22px] text-[16px] leading-[1.65] text-dark-textSecondary">
        {items.map((item, i) => (
            <li key={i}>{item}</li>
        ))}
    </ul>
);

/** Callout — v1: #292E31 with a 3px #DDDD61 left rule, used for the medical notice. */
export const LegalCallout: React.FC<{ title: string; children: React.ReactNode }> = ({
    title,
    children,
}) => (
    <div className="mb-3.5 rounded-2xl border-l-[3px] border-[#DDDD61] bg-dark-gunmetal p-5">
        <p className="mb-1.5 font-semibold text-[#DDDD61]">{title}</p>
        <p className="text-[16px] leading-[1.65] text-dark-textSecondary">{children}</p>
    </div>
);

/** Inline mail link. */
export const LegalMail: React.FC<{ email: string }> = ({ email }) => (
    <a
        href={`mailto:${email}`}
        className="text-[#0393E7] transition-colors hover:text-success"
    >
        {email}
    </a>
);

/**
 * Contact block — v1 renders this as a raised card (#202428, radius 20px)
 * with a blue CTA button rather than a plain section.
 */
export const LegalContact: React.FC<{
    label?: string;
    title: string;
    children: React.ReactNode;
    email: string;
}> = ({ label, title, children, email }) => (
    <section className="mt-12 rounded-[20px] bg-[#202428] p-[clamp(24px,4vw,34px)]">
        <h2 className="mb-3 text-2xl font-bold tracking-[-0.02em] text-white">
            {label && <span className="text-success">{label}</span>}
            {label && ' '}
            {title}
        </h2>
        <div className="mb-[18px] text-[16px] leading-[1.65] text-dark-textSecondary">
            {children}
        </div>
        <a
            href={`mailto:${email}`}
            className="inline-flex h-12 items-center gap-2.5 rounded-xl bg-accent-bright px-5 text-sm font-bold tracking-[0.06em] text-white transition-colors hover:bg-accent"
        >
            {email}
        </a>
    </section>
);

export default LegalLayout;
